import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { BrandStyleGuide } from '../../../types';
import { Save, RefreshCw, Plus, Trash2, Layout, Type, Palette, Globe, X } from 'lucide-react';

interface BrandStyleEditorProps {
    onClose: () => void;
}

const defaultStyle: BrandStyleGuide = {
    companyName: '4HVNSK8',
    logo: {
        url: 'https://hvnsk8.web.app/assets/logo/4HVNSK8-logo-black.png',
        alt: '4HVNSK8',
        height: 40,
        invertOnDark: true,
    },
    siteUrl: 'https://hvnsk8.web.app',
    colors: {
        headerBg:   '#111111',
        footerBg:   '#111111',
        bodyBg:     '#f4f4f4',
        cardBg:     '#ffffff',
        primary:    '#111111',
        bodyText:   '#333333',
        mutedText:  '#666666',
        subtleText: '#555555',
        linkText:   '#999999',
        border:     '#dddddd',
        surfaceBg:  '#f9f9f9',
        buttonBg:   '#111111',
        buttonText: '#ffffff',
    },
    typography: {
        fontFamily:     "'Helvetica Neue', Arial, sans-serif",
        headingSize:    22,
        bodySize:       16,
        smallSize:      12,
        tinySize:       11,
        lineHeight:     1.6,
        letterSpacing:  '2px',
    },
    layout: {
        containerWidth: 600,
        headerPadding:  '30px 20px',
        bodyPadding:    '40px 30px',
        footerPadding:  '25px 20px',
        borderRadius:   4,
    },
    socials: [
        { label: 'Instagram', url: 'https://www.instagram.com/4hvnsk8' },
        { label: 'Twitter',   url: 'https://x.com/4hvnsk8' },
        { label: 'YouTube',   url: 'https://www.youtube.com/@4hvnsk8' },
    ],
};

const BrandStyleEditor: React.FC<BrandStyleEditorProps> = ({ onClose }) => {
    const [style, setStyle] = useState<BrandStyleGuide>(defaultStyle);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeSection, setActiveSection] = useState<string>('general');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const docRef = doc(db, 'settings', 'email_branding');
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                // Merge with default to ensure structural integrity
                setStyle({ ...defaultStyle, ...snap.data() } as BrandStyleGuide);
            }
        } catch (error) {
            console.error("Error fetching branding settings:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'settings', 'email_branding'), style);
            alert("Brand settings saved successfully!");
        } catch (error) {
            console.error("Error saving settings:", error);
            alert("Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (confirm("Are you sure you want to reset to default settings? Unsaved changes will be lost.")) {
            setStyle(defaultStyle);
        }
    };

    const updateColor = (key: keyof BrandStyleGuide['colors'], value: string) => {
        setStyle(prev => ({
            ...prev,
            colors: {
                ...prev.colors,
                [key]: value
            }
        }));
    };

    const updateTypography = (key: keyof BrandStyleGuide['typography'], value: any) => {
        setStyle(prev => ({
            ...prev,
            typography: {
                ...prev.typography,
                [key]: value
            }
        }));
    };

    const updateLayout = (key: keyof BrandStyleGuide['layout'], value: any) => {
        setStyle(prev => ({
            ...prev,
            layout: {
                ...prev.layout,
                [key]: value
            }
        }));
    };

    // --- Preview Renderer ---
    const renderPreview = () => {
        const s = style;
        const logoFilter = s.logo.invertOnDark ? 'filter: invert(1);' : '';
        
        // Inline styles for preview
        const styles = {
            container: {
                fontFamily: s.typography.fontFamily,
                backgroundColor: s.colors.bodyBg,
                padding: '40px 20px',
                minHeight: '100%',
            },
            card: {
                backgroundColor: s.colors.cardBg,
                maxWidth: `${s.layout.containerWidth}px`,
                margin: '0 auto',
                borderRadius: `${s.layout.borderRadius}px`, // Apply generally for preview via overflow-hidden on table equivalent
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            },
            header: {
                backgroundColor: s.colors.headerBg,
                padding: s.layout.headerPadding,
                textAlign: 'center' as const,
            },
            body: {
                padding: s.layout.bodyPadding,
                color: s.colors.bodyText,
                fontSize: `${s.typography.bodySize}px`,
                lineHeight: s.typography.lineHeight,
            },
            footer: {
                backgroundColor: s.colors.footerBg,
                padding: s.layout.footerPadding,
                textAlign: 'center' as const,
                color: s.colors.subtleText,
                fontSize: `${s.typography.tinySize}px`,
            },
            heading: {
                color: s.colors.primary,
                fontSize: `${s.typography.headingSize}px`,
                margin: '0 0 20px 0',
                fontWeight: 'bold',
            },
            button: {
                display: 'inline-block',
                backgroundColor: s.colors.buttonBg,
                color: s.colors.buttonText,
                padding: '15px 40px',
                textDecoration: 'none',
                fontSize: '14px',
                letterSpacing: s.typography.letterSpacing,
                textTransform: 'uppercase' as const,
                fontWeight: 'bold',
                marginTop: '20px',
            },
            link: {
                color: s.colors.linkText,
                textDecoration: 'none',
                margin: '0 8px',
                fontSize: `${s.typography.smallSize}px`,
            }
        };

        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    {/* Header */}
                    <div style={styles.header}>
                        <img 
                            src={s.logo.url} 
                            alt={s.logo.alt} 
                            style={{ 
                                height: `${s.logo.height}px`, 
                                width: 'auto',
                                filter: s.logo.invertOnDark ? 'invert(1)' : 'none'
                             }} 
                        />
                    </div>

                    {/* Body */}
                    <div style={styles.body}>
                        <h2 style={styles.heading}>Order Confirmation</h2>
                        <p style={{ margin: '0 0 15px 0' }}>Hi Andrew, thanks for your order!</p>
                        
                        <div style={{ 
                            backgroundColor: s.colors.surfaceBg, 
                            padding: '20px', 
                            borderRadius: `${s.layout.borderRadius}px`,
                            marginBottom: '25px',
                            border: `1px solid ${s.colors.border}`
                        }}>
                             <div className="flex justify-between items-center mb-4 border-b pb-2" style={{ borderColor: s.colors.border }}>
                                <span style={{ color: s.colors.mutedText, fontSize: '14px' }}>Item</span>
                                <span style={{ color: s.colors.mutedText, fontSize: '14px' }}>Price</span>
                             </div>
                             <div className="flex justify-between items-center mb-2">
                                <span>Logo Tee - Black / L</span>
                                <span>$35.00</span>
                             </div>
                             <div className="flex justify-between items-center font-bold mt-4 pt-2 border-t" style={{ borderColor: s.colors.border, color: s.colors.primary }}>
                                <span>Total</span>
                                <span>$35.00</span>
                             </div>
                        </div>

                        <p>We're getting it ready to ship.</p>
                        
                        <div style={{ textAlign: 'center' }}>
                            <a href="#" style={styles.button}>View Order</a>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={styles.footer}>
                        <div style={{ marginBottom: '15px' }}>
                            {s.socials.map((social, i) => (
                                <a key={i} href={social.url} style={styles.link}>{social.label}</a>
                            ))}
                        </div>
                        <p>&copy; {new Date().getFullYear()} {s.companyName}. All rights reserved.</p>
                        <div style={{ marginTop: '10px' }}>
                             <a href="#" style={{ color: s.colors.mutedText, textDecoration: 'underline' }}>Unsubscribe</a>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) return <div>Loading settings...</div>;

    return (
        <div className="fixed inset-0 bg-white z-[60] flex flex-col">
            {/* Toolbar */}
            <div className="h-16 border-b border-gray-200 px-6 flex items-center justify-between bg-white shrink-0">
                <div className="flex items-center gap-4">
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                        <X size={20} />
                    </button>
                    <h2 className="font-heading text-xl tracking-widest font-bold">BRAND STYLE EDITOR</h2>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={handleReset}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider text-gray-500 hover:text-red-600 transition-colors"
                    >
                        <RefreshCw size={16} />
                        Reset Default
                    </button>
                    <button 
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                        <Save size={16} />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
                {/* Editor Panel */}
                <div className="w-[400px] border-r border-gray-200 overflow-y-auto bg-gray-50 flex flex-col">
                    {/* Tabs / Sections */}
                    <div className="flex border-b border-gray-200 bg-white sticky top-0 z-10">
                        <button 
                            onClick={() => setActiveSection('general')} 
                            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider flex flex-col items-center gap-1 ${activeSection === 'general' ? 'border-b-2 border-black text-black' : 'text-gray-400 hover:text-black'}`}
                        >
                            <Globe size={16} /> General
                        </button>
                        <button 
                            onClick={() => setActiveSection('colors')} 
                            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider flex flex-col items-center gap-1 ${activeSection === 'colors' ? 'border-b-2 border-black text-black' : 'text-gray-400 hover:text-black'}`}
                        >
                            <Palette size={16} /> Colors
                        </button>
                        <button 
                            onClick={() => setActiveSection('type')} 
                            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider flex flex-col items-center gap-1 ${activeSection === 'type' ? 'border-b-2 border-black text-black' : 'text-gray-400 hover:text-black'}`}
                        >
                            <Type size={16} /> Type
                        </button>
                         <button 
                            onClick={() => setActiveSection('layout')} 
                            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider flex flex-col items-center gap-1 ${activeSection === 'layout' ? 'border-b-2 border-black text-black' : 'text-gray-400 hover:text-black'}`}
                        >
                            <Layout size={16} /> Layout
                        </button>
                    </div>

                    <div className="p-6 space-y-8">
                        {activeSection === 'general' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Company Name</label>
                                    <input 
                                        type="text" 
                                        value={style.companyName}
                                        onChange={e => setStyle({...style, companyName: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Website URL</label>
                                    <input 
                                        type="text" 
                                        value={style.siteUrl}
                                        onChange={e => setStyle({...style, siteUrl: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                    />
                                </div>
                                <div className="pt-4 border-t border-gray-200">
                                    <h3 className="font-bold text-sm mb-3">Logo Config</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Logo Image URL</label>
                                            <input 
                                                type="text" 
                                                value={style.logo.url}
                                                onChange={e => setStyle({...style, logo: {...style.logo, url: e.target.value}})}
                                                className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                            />
                                        </div>
                                        <div className="flex gap-4">
                                            <div className="flex-1">
                                                <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Height (px)</label>
                                                <input 
                                                    type="number" 
                                                    value={style.logo.height}
                                                    onChange={e => setStyle({...style, logo: {...style.logo, height: parseInt(e.target.value) || 0}})}
                                                    className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                                />
                                            </div>
                                            <div className="flex items-center pt-5">
                                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={style.logo.invertOnDark}
                                                        onChange={e => setStyle({...style, logo: {...style.logo, invertOnDark: e.target.checked}})}
                                                        className="h-4 w-4"
                                                    />
                                                    Invert on dark theme
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeSection === 'colors' && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="font-bold text-xs uppercase mb-3 border-b pb-1">Base Colors</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        {Object.entries({
                                            'Background': 'bodyBg',
                                            'Card Bg': 'cardBg',
                                            'Header Bg': 'headerBg',
                                            'Footer Bg': 'footerBg'
                                        }).map(([label, key]) => (
                                            <div key={key}>
                                                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                                                <div className="flex items-center gap-2">
                                                    <input 
                                                        type="color" 
                                                        value={style.colors[key as keyof typeof style.colors]}
                                                        onChange={e => updateColor(key as any, e.target.value)}
                                                        className="h-8 w-8 rounded cursor-pointer border-none bg-transparent p-0"
                                                    />
                                                    <span className="text-xs font-mono">{style.colors[key as keyof typeof style.colors]}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-bold text-xs uppercase mb-3 border-b pb-1">Text Colors</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        {Object.entries({
                                            'Primary / Heading': 'primary',
                                            'Body Text': 'bodyText',
                                            'Muted / Secondary': 'mutedText',
                                            'Subtle / Footer': 'subtleText',
                                            'Links': 'linkText'
                                        }).map(([label, key]) => (
                                            <div key={key}>
                                                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                                                <div className="flex items-center gap-2">
                                                    <input 
                                                        type="color" 
                                                        value={style.colors[key as keyof typeof style.colors]}
                                                        onChange={e => updateColor(key as any, e.target.value)}
                                                        className="h-8 w-8 rounded cursor-pointer border-none bg-transparent p-0"
                                                    />
                                                    <span className="text-xs font-mono">{style.colors[key as keyof typeof style.colors]}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-bold text-xs uppercase mb-3 border-b pb-1">Elements</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        {Object.entries({
                                            'Button Bg': 'buttonBg',
                                            'Button Text': 'buttonText',
                                            'Border': 'border',
                                            'Highlight Surface': 'surfaceBg'
                                        }).map(([label, key]) => (
                                            <div key={key}>
                                                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                                                <div className="flex items-center gap-2">
                                                    <input 
                                                        type="color" 
                                                        value={style.colors[key as keyof typeof style.colors]}
                                                        onChange={e => updateColor(key as any, e.target.value)}
                                                        className="h-8 w-8 rounded cursor-pointer border-none bg-transparent p-0"
                                                    />
                                                    <span className="text-xs font-mono">{style.colors[key as keyof typeof style.colors]}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeSection === 'type' && (
                             <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Font Family</label>
                                    <input 
                                        type="text" 
                                        value={style.typography.fontFamily}
                                        onChange={e => updateTypography('fontFamily', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                        placeholder="Arial, sans-serif"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Heading Size (px)</label>
                                        <input 
                                            type="number" 
                                            value={style.typography.headingSize}
                                            onChange={e => updateTypography('headingSize', parseInt(e.target.value))}
                                            className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Body Size (px)</label>
                                        <input 
                                            type="number" 
                                            value={style.typography.bodySize}
                                            onChange={e => updateTypography('bodySize', parseInt(e.target.value))}
                                            className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Line Height</label>
                                        <input 
                                            type="number" 
                                            step="0.1"
                                            value={style.typography.lineHeight}
                                            onChange={e => updateTypography('lineHeight', parseFloat(e.target.value))}
                                            className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Letter Spacing</label>
                                        <input 
                                            type="text" 
                                            value={style.typography.letterSpacing}
                                            onChange={e => updateTypography('letterSpacing', e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                            placeholder="1px"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeSection === 'layout' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Max Width (px)</label>
                                    <input 
                                        type="number" 
                                        value={style.layout.containerWidth}
                                        onChange={e => updateLayout('containerWidth', parseInt(e.target.value))}
                                        className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                     <div>
                                        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Border Radius</label>
                                        <input 
                                            type="number" 
                                            value={style.layout.borderRadius}
                                            onChange={e => updateLayout('borderRadius', parseInt(e.target.value))}
                                            className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Body Padding</label>
                                    <input 
                                        type="text" 
                                        value={style.layout.bodyPadding}
                                        onChange={e => updateLayout('bodyPadding', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Header Padding</label>
                                    <input 
                                        type="text" 
                                        value={style.layout.headerPadding}
                                        onChange={e => updateLayout('headerPadding', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded text-sm font-body"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Preview Panel */}
                <div className="flex-1 bg-gray-200 overflow-y-auto p-12 flex justify-center sticky top-0 h-full">
                    <div className="w-full max-w-[800px] shadow-2xl min-h-[500px] bg-white">
                         {renderPreview()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrandStyleEditor;
